/**
 * StaffLegend.tsx
 * The key for the "Staff this job" v2 view (STAFF-VIEW-V2.md §5): what the skill
 * badges mean, the weekly-capacity bands, and the work/absence signals. Colors
 * via theme tokens — no hex literals (CODING-STANDARDS).
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

interface Item { color: string; label: string; hollow?: boolean }

const Swatch: React.FC<Item> = ({ color, label, hollow }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
    <Box
      sx={{
        width: 12, height: 12, borderRadius: '3px',
        backgroundColor: hollow ? 'transparent' : color,
        border: `1.5px solid ${color}`,
      }}
    />
    <Typography variant="caption" sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>{label}</Typography>
  </Box>
);

const Group: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
    <Typography variant="caption" sx={{ fontSize: '0.64rem', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

export const StaffLegend: React.FC = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex', flexDirection: 'column', gap: 0.6,
        px: 1.25, py: 1,
        borderRadius: `${theme.border.radius}px`,
        border: `1px solid ${theme.border.color}`,
        backgroundColor: alpha(theme.palette.text.primary, 0.02),
      }}
    >
      {/* On the person cards the skill badge is wordless — just the L-number in a
          colored pill — so this key explains the colors (BUG-2026-0141 #5). */}
      <Group title="Skill badge (L#)">
        <Swatch color={theme.schedule.skillQualified} label="Green L3+ — qualified" />
        <Swatch color={theme.schedule.skillMentor} label="Amber L1/L2 — needs mentor" />
        <Swatch color={theme.schedule.skillUnqualified} label="Red L? — not qualified" />
        <Swatch color={theme.palette.text.disabled} label="Grey (—) no skill needed" hollow />
      </Group>
      <Group title="Weekly load">
        <Swatch color={theme.schedule.allocUnder} label="Under" />
        <Swatch color={theme.schedule.allocHealthy} label="Healthy" />
        <Swatch color={theme.schedule.allocAt} label="At capacity" />
        <Swatch color={theme.schedule.allocOver} label="Over (warn only)" />
      </Group>
      <Group title="Signals">
        <Swatch color={theme.schedule.maintenance} label="Unstaffed / needs mentor" />
        <Swatch color={theme.schedule.employeeAlloc} label="On the job" />
      </Group>
    </Box>
  );
};
